/**
 * Pipeline step that runs an ordered action pipeline through the Arquero
 * executor.
 *
 * The LLM-facing `transformDataset` tool targets this step directly. Each
 * action declared in {@link PipelineSpec.actions} dispatches to its
 * per-type executor inside {@link transformDataset}; this wrapper handles
 * the pre-pass concerns (filter source-ref resolution against the
 * pipeline-context resolver) and propagates engine warnings / aggregation
 * envelopes onto the returned dataset's meta so the tool layer can surface
 * them to the LLM.
 */
import { createDataset } from '../dataset.ts';
import { transformDataset } from '../engine/arquero-executor.ts';
import { type Action, type PipelineSpec, pipelineSchema } from '../engine/pipeline-schema.ts';
import { resolveFilterSourceRefs } from '../filter/resolve-source-refs.ts';
import type { RootFilter } from '../filter/schema.ts';
import type { PipelineContext, PipelineStep } from '../step/types.ts';
import type { Dataset, DatasetMeta } from '../types.ts';
import { MissingPipelineInputError } from '#plugin-chat/lib/tool/errors/index.ts';

const STEP_ID = 'transform';

/**
 * Resolve `sourceRef` leaves in any filter actions. Source-ref resolution
 * pulls staged-dataset values via the pipeline-context resolver and uses
 * the input dataset's column schema as the type oracle. When no resolver
 * is present (tests / non-pipeline callers) the actions pass through
 * unchanged — raw `sourceRef` leaves would still reach `transformDataset`
 * in that case, but the code paths that care (tool layer) always supply
 * a resolver.
 */
async function resolveActionSourceRefs(
  actions: readonly Action[],
  input: Dataset,
  ctx: PipelineContext,
): Promise<readonly Action[]> {
  if (!ctx.resolve) {
    return actions;
  }
  const resolve = ctx.resolve;
  return Promise.all(
    actions.map(async (action): Promise<Action> => {
      if (action?.type !== 'filter') {
        return action;
      }
      const resolvedWhere = (await resolveFilterSourceRefs(action.where as RootFilter, {
        resolve,
        getTargetType: (columnName: string) => input.columns.find((c) => c.name === columnName)?.type,
        stepId: STEP_ID,
        contextLabel: 'transform action',
      })) as Action extends { type: 'filter'; where: infer W } ? W : never;
      return { ...action, where: resolvedWhere };
    }),
  );
}

export const transformStep: PipelineStep<PipelineSpec> = {
  id: STEP_ID,
  description:
    'Run an ordered pipeline of dataset actions. ' +
    "Each action receives the previous action's output as its input. " +
    'Every column-reference field accepts an inline math.js expression as well as a literal column name.',
  paramsSchema: pipelineSchema,
  consumesInput: true,
  execute: async (params, input, ctx) => {
    // console.log('Executing transform step', { params, input });
    // console.trace();
    if (!input) {
      throw new MissingPipelineInputError(STEP_ID);
    }

    const resolvedActions = await resolveActionSourceRefs(params.actions, input, ctx);
    const resolvedSpec: PipelineSpec = { ...params, actions: [...resolvedActions] };

    // transformDataset throws ToolExecutionError subclasses on failure;
    // we let them propagate to the tool boundary unchanged.
    const result = await transformDataset(
      input,
      resolvedSpec,
      ctx.client && ctx.queryClient ? { client: ctx.client, queryClient: ctx.queryClient } : undefined,
    );

    const meta: DatasetMeta = {
      ...result.dataset.meta,
      ...(result.warnings === undefined ? {} : { warnings: result.warnings }),
      ...(result.aggregations === undefined ? {} : { aggregations: result.aggregations }),
      ...(result.groupedAggregations === undefined ? {} : { groupedAggregations: result.groupedAggregations }),
    };

    return createDataset({
      rows: result.dataset.rows,
      columns: result.dataset.columns,
      meta,
    });
  },
};
